import {
  CHECK_KIND_LEAF,
  CheckExpression,
  CheckLeaf,
  LeafCheck
} from "lib/types/workflow"
import {
  COMPARISON_TYPES,
  ComparisonString,
  ComparisonInteger,
  ComparisonType,
  MAX_COMPARISON_VALUES,
  MULTI_VALUE_COMPARISONS,
  NodeConditionCheckAddressFields
} from "lib/types/workflowConditions"
import {
  CHECK_DESCRIPTORS,
  CheckDescriptor,
  FieldDescriptor
} from "./conditionSchema"
import { getAtPath } from "./checkFieldHelpers"

// ──────────────────────────────────────────────────────────────────────────
// Comparison option lists
// ──────────────────────────────────────────────────────────────────────────

export const COMPARISON_LABELS: Record<ComparisonType, string> = {
  EQUALS: "Equals",
  NOT_EQUALS: "Not equals",
  // CONTAINS / NOT_CONTAINS are list-membership operators on the backend
  // (the field value must be one of / none of the listed values), not
  // substring operators. The clarifier prevents users from expecting
  // substring matching.
  CONTAINS: "Contains (is one of)",
  NOT_CONTAINS: "Not contains (is not one of)",
  LESS_THAN: "Less than",
  LESS_THAN_EQUALS: "Less than or equal",
  GREATER_THAN: "Greater than",
  GREATER_THAN_EQUALS: "Greater than or equal",
  BEGINS_WITH: "Begins with",
  ENDS_WITH: "Ends with"
}

export const COMPARISONS: { value: ComparisonType; label: string }[] =
  COMPARISON_TYPES.map((c) => ({ value: c, label: COMPARISON_LABELS[c] }))

/** String comparisons (no numeric ones). */
export const STRING_COMPARISONS = COMPARISONS.filter(
  (c) =>
    c.value !== "LESS_THAN" &&
    c.value !== "LESS_THAN_EQUALS" &&
    c.value !== "GREATER_THAN" &&
    c.value !== "GREATER_THAN_EQUALS"
)

/** Integer comparisons (drop string-only ones). */
export const INTEGER_COMPARISONS = COMPARISONS.filter(
  (c) => c.value !== "BEGINS_WITH" && c.value !== "ENDS_WITH"
)

/** Datetime comparisons — same as integers (ordered, supports ranges). */
export const DATETIME_COMPARISONS = INTEGER_COMPARISONS

export const isMultiValueComparison = (c: ComparisonType): boolean =>
  MULTI_VALUE_COMPARISONS.has(c)

// ──────────────────────────────────────────────────────────────────────────
// Email helpers (used by the ADDRESSES email field)
// ──────────────────────────────────────────────────────────────────────────

export const parseEmailList = (raw: string): string[] =>
  Array.from(
    new Set(
      raw
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
    )
  )

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export const findInvalidEmails = (emails: string[]): string[] =>
  emails.filter((e) => !EMAIL_RE.test(e))

// ──────────────────────────────────────────────────────────────────────────
// Misc helpers
// ──────────────────────────────────────────────────────────────────────────

export const isValidJson = (value: string): boolean => {
  if (!value.trim()) return true
  try {
    JSON.parse(value)
    return true
  } catch {
    return false
  }
}

const HTTPS_RE = /^https:\/\/.+/

// ──────────────────────────────────────────────────────────────────────────
// Field-level validity
// ──────────────────────────────────────────────────────────────────────────

export const isComparisonStringValid = (cs: ComparisonString | undefined): boolean => {
  if (!cs) return false
  if (!cs.values?.length || cs.values.length > MAX_COMPARISON_VALUES) return false
  return cs.values.every((v) => v.trim().length > 0)
}

export const isComparisonIntegerValid = (
  ci: ComparisonInteger | undefined
): boolean => {
  if (!ci) return false
  if (!ci.values?.length || ci.values.length > MAX_COMPARISON_VALUES) return false
  return ci.values.every((v) => Number.isFinite(v) && v >= 0)
}

const isAddressFieldsValid = (a: NodeConditionCheckAddressFields | undefined): boolean => {
  if (!a) return false
  const entries = Object.entries(a) as [keyof NodeConditionCheckAddressFields, ComparisonString | undefined][]
  const set = entries.filter(([, v]) => v !== undefined)
  if (set.length === 0) return false
  return set.every(([, v]) => isComparisonStringValid(v))
}

// ──────────────────────────────────────────────────────────────────────────
// Whole-leaf validity (consults the descriptor for shape + minProperties)
// ──────────────────────────────────────────────────────────────────────────

/** A leaf is valid iff it has zero problems. The single source of truth is
 *  `getLeafCheckProblems` below — both functions walk the same shapes. */
export const isLeafCheckValid = (check: LeafCheck): boolean =>
  getLeafCheckProblems(check).length === 0

export const isLeafValid = (leaf: CheckLeaf): boolean =>
  isLeafCheckValid(leaf.check)

// ──────────────────────────────────────────────────────────────────────────
// Human-readable validation problems (for surfacing why Save is disabled)
// ──────────────────────────────────────────────────────────────────────────

const fieldLabel = (descriptor: CheckDescriptor, path: string): string => {
  const f = descriptor.fields?.find((x) => x.path === path)
  if (!f) return path
  return f.group ? `${f.group} — ${f.label}` : f.label
}

const fieldValueProblems = (
  descriptor: CheckDescriptor,
  check: LeafCheck,
  field: FieldDescriptor
): string[] => {
  const root = (check as { fields?: unknown }).fields
  const value = getAtPath(root, field.path)
  const label = fieldLabel(descriptor, field.path)
  if (value === undefined) {
    return [`${label} is missing`]
  }
  switch (field.kind) {
    case "comparisonString":
    case "comparisonStringEnum":
      if (!isComparisonStringValid(value as ComparisonString)) {
        return [`${label} needs at least one non-empty value`]
      }
      return []
    case "comparisonInteger":
      if (!isComparisonIntegerValid(value as ComparisonInteger)) {
        return [`${label} needs at least one numeric value (≥ 0)`]
      }
      return []
    case "arrayCount":
      if (
        !isComparisonIntegerValid(
          (value as { length?: ComparisonInteger }).length
        )
      ) {
        return [`${label} needs a length comparison value`]
      }
      return []
    case "keyValues": {
      const list = (value as { keyValues?: unknown[] }).keyValues
      if (!Array.isArray(list) || list.length === 0) {
        return [`${label} needs at least one key/value pair`]
      }
      const problems: string[] = []
      list.forEach((kv, idx) => {
        const pair = kv as {
          key?: ComparisonString
          value?: ComparisonString
        }
        const hasKey = pair.key !== undefined
        const hasValue = pair.value !== undefined
        if (!hasKey && !hasValue) {
          problems.push(`${label} pair ${idx + 1}: set a key or value`)
          return
        }
        if (hasKey && !isComparisonStringValid(pair.key)) {
          problems.push(`${label} pair ${idx + 1}: key needs a value`)
        }
        if (hasValue && !isComparisonStringValid(pair.value)) {
          problems.push(`${label} pair ${idx + 1}: value needs a value`)
        }
      })
      return problems
    }
  }
}

export const getLeafCheckProblems = (check: LeafCheck): string[] => {
  const descriptor: CheckDescriptor | undefined = CHECK_DESCRIPTORS[check.type]
  if (!descriptor) return ["Unknown check type"]

  switch (descriptor.shape) {
    case "compound":
      return ["Compound checks are not valid leaves"]
    case "endpoint": {
      const c = check as Extract<LeafCheck, { type: "CUSTOM_HTTP" }>
      const out: string[] = []
      if (!c.endpoint) out.push("Endpoint URL is required")
      else if (!HTTPS_RE.test(c.endpoint))
        out.push("Endpoint must be an HTTPS URL")
      return out
    }
    case "single-datetime": {
      const c = check as Extract<LeafCheck, { type: "ORDER_DATE" }>
      const out: string[] = []
      if (!c.comparison) out.push("Comparison is required")
      if (!c.values?.length) {
        out.push("At least one date is required")
      } else if (c.values.length > MAX_COMPARISON_VALUES) {
        out.push(`No more than ${MAX_COMPARISON_VALUES} dates allowed`)
      } else if (
        !c.values.every((v) => !Number.isNaN(new Date(v).getTime()))
      ) {
        out.push("All dates must be parseable (e.g. 2026-05-04T12:00:00Z)")
      }
      return out
    }
    case "array-count": {
      const c = check as Extract<
        LeafCheck,
        { type: "ORDER_LINES" | "SHIPPING_FEES" | "GIFTS" }
      >
      if (!isComparisonIntegerValid(c.count?.length)) {
        return ["Length needs at least one numeric value (≥ 0)"]
      }
      return []
    }
    case "field-bag": {
      const fields = (check as { fields?: Record<string, unknown> }).fields
      if (!fields) return ["Add at least one field"]
      const setFields = (descriptor.fields ?? []).filter(
        (f) => getAtPath(fields, f.path) !== undefined
      )
      if (setFields.length === 0) return ["Add at least one field"]
      return setFields.flatMap((f) => fieldValueProblems(descriptor, check, f))
    }
    case "address-bag": {
      const c = check as Extract<LeafCheck, { type: "ADDRESSES" }>
      const fields = c.fields
      if (
        !fields ||
        (fields.billingAddress === undefined &&
          fields.shippingAddress === undefined)
      ) {
        return ["Add at least one billing or shipping field"]
      }
      const out: string[] = []
      if (
        fields.billingAddress !== undefined &&
        !isAddressFieldsValid(fields.billingAddress)
      ) {
        out.push("Every set billing field needs a non-empty value")
      }
      if (
        fields.shippingAddress !== undefined &&
        !isAddressFieldsValid(fields.shippingAddress)
      ) {
        out.push("Every set shipping field needs a non-empty value")
      }
      return out
    }
  }
}

export const getExpressionProblems = (expr: CheckExpression): string[] => {
  if (expr.kind === CHECK_KIND_LEAF) return getLeafCheckProblems(expr.check)
  if (expr.children.length < 2) {
    return ["Group needs at least 2 checks (or unwrap it)"]
  }
  return expr.children.flatMap(getExpressionProblems)
}

